import { Building2, CreditCard, ImagePlus, Save, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { StatusNotice } from '../components/StatusNotice';
import { FormSelectMenu } from '../components/ui/FormSelectMenu';
import { api } from '../lib/api';

interface DashboardData { profile: Record<string, any>; }
interface PaymentDestination { id: string; destination_type: string; provider: string; account_name: string; account_number_masked: string; verification_status: string; is_default: number; }
interface AgencyMember { user_id: string; name: string; email: string; role: string; status: string; }

const paymentProviders = [
  'MTN MoMo', 'Telecel Cash', 'AT Money', 'GCB Bank', 'Ecobank Ghana',
  'Fidelity Bank Ghana', 'Absa Bank Ghana', 'Stanbic Bank Ghana', 'CalBank',
];

export function AgencySettingsPage() {
  const [profile, setProfile] = useState<Record<string, any> | null>(null);
  const [destinations, setDestinations] = useState<PaymentDestination[]>([]);
  const [members, setMembers] = useState<AgencyMember[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const logoRef = useRef<HTMLInputElement>(null);
  const coverRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const [dashboard, paymentRows, memberRows] = await Promise.all([
        api.get<DashboardData>('/api/agency/dashboard'),
        api.get<PaymentDestination[]>('/api/agency/payment-destinations'),
        api.get<AgencyMember[]>('/api/agency/members'),
      ]);
      setProfile(dashboard.profile);
      setDestinations(paymentRows);
      setMembers(memberRows);
    } catch (cause) {
      setNotice({ type: 'error', message: cause instanceof Error ? cause.message : 'Could not load agency settings.' });
    }
  };

  useEffect(() => { void load(); }, []);

  const saveProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true); setNotice(null);
    try {
      const form = new FormData(event.currentTarget);
      const logo = logoRef.current?.files?.[0];
      const cover = coverRef.current?.files?.[0];
      const [logoUpload, coverUpload] = await Promise.all([
        logo ? api.upload(logo, 'agency_logo') : Promise.resolve(null),
        cover ? api.upload(cover, 'agency_cover') : Promise.resolve(null),
      ]);
      await api.patch('/api/agency/profile', {
        name: String(form.get('name') || ''),
        description: String(form.get('description') || ''),
        email: String(form.get('email') || ''),
        phone: String(form.get('phone') || ''),
        whatsapp: String(form.get('whatsapp') || ''),
        websiteUrl: String(form.get('websiteUrl') || ''),
        ...(logoUpload ? { logoKey: logoUpload.objectKey } : {}),
        ...(coverUpload ? { coverKey: coverUpload.objectKey } : {}),
      });
      setNotice({ type: 'success', message: 'Agency branding and profile saved.' });
      await load();
      if (logoRef.current) logoRef.current.value = '';
      if (coverRef.current) coverRef.current.value = '';
    } catch (cause) {
      setNotice({ type: 'error', message: cause instanceof Error ? cause.message : 'Could not save the agency profile.' });
    } finally { setBusy(false); }
  };

  const addDestination = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setNotice(null);
    try {
      const form = new FormData(event.currentTarget);
      await api.post('/api/agency/payment-destinations', {
        destinationType: String(form.get('destinationType')),
        provider: String(form.get('provider')),
        accountName: String(form.get('accountName')),
        accountNumber: String(form.get('accountNumber')),
        isDefault: form.get('isDefault') === 'on',
      });
      event.currentTarget.reset();
      setNotice({ type: 'success', message: 'Payment destination submitted for verification.' });
      await load();
    } catch (cause) {
      setNotice({ type: 'error', message: cause instanceof Error ? cause.message : 'Could not add the payment destination.' });
    } finally { setBusy(false); }
  };

  const inviteMember = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setNotice(null);
    try {
      const form = new FormData(event.currentTarget);
      await api.post('/api/agency/members/invite', { email: String(form.get('email')), role: String(form.get('role')) });
      event.currentTarget.reset();
      setNotice({ type: 'success', message: 'Team invitation sent.' });
    } catch (cause) {
      setNotice({ type: 'error', message: cause instanceof Error ? cause.message : 'Could not send the invitation.' });
    } finally { setBusy(false); }
  };

  if (!profile) return <div className="page"><div className="loading-state"><span className="spinner" />Loading agency settings…</div>{notice && <StatusNotice type={notice.type}>{notice.message}</StatusNotice>}</div>;
  const initials = String(profile.name || 'Agency').split(/\s+/).map((part: string) => part[0]).join('').slice(0, 2).toUpperCase();

  return <div className="page agency-settings-page">
    <PageHeader eyebrow="Agency workspace" title="Agency settings" description="Control your public identity, team access and verified payment destinations." />
    <div className="workspace-tabs"><Link to="/agency">Overview</Link><Link to="/agency/hostels">Hostels & offers</Link><Link to="/agency/requests">Matched requests</Link><Link to="/agency/verification">Verification</Link><Link className="active" to="/agency/settings">Settings</Link></div>
    {notice && <StatusNotice type={notice.type}>{notice.message}</StatusNotice>}

    <div className="agency-settings-grid">
      <form className="surface-section agency-profile-form" onSubmit={saveProfile}>
        <div className="section-heading compact"><div><span className="eyebrow">Public identity</span><h2>Agency branding</h2></div><Building2 size={22} /></div>
        <div className="agency-branding-editor">
          <div className="agency-cover-preview">{profile.cover_key ? <img src={`/media/${profile.cover_key}`} alt="Agency cover" /> : <span>Cover image</span>}</div>
          <div className="agency-logo-preview">{profile.logo_key ? <img src={`/media/${profile.logo_key}`} alt="Agency logo" /> : <strong>{initials}</strong>}</div>
        </div>
        <div className="branding-upload-grid">
          <label><span><ImagePlus size={15} /> Agency logo</span><input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" /><small>Square PNG, JPG or WebP works best.</small></label>
          <label><span><ImagePlus size={15} /> Cover image</span><input ref={coverRef} type="file" accept="image/png,image/jpeg,image/webp" /><small>Use a wide image of your team or service.</small></label>
        </div>
        <label><span>Agency name</span><input name="name" defaultValue={profile.name || ''} required minLength={2} /></label>
        <label><span>Public description</span><textarea name="description" rows={5} defaultValue={profile.description || ''} placeholder="Tell students what your agency does and the campuses you serve." /></label>
        <div className="form-grid">
          <label><span>Email</span><input name="email" type="email" defaultValue={profile.email || ''} required /></label>
          <label><span>Phone</span><input name="phone" type="tel" defaultValue={profile.phone || ''} /></label>
          <label><span>WhatsApp</span><input name="whatsapp" type="tel" defaultValue={profile.whatsapp || ''} /></label>
          <label><span>Website</span><input name="websiteUrl" type="url" defaultValue={profile.website_url || ''} placeholder="https://" /></label>
        </div>
        <button className="primary-button" disabled={busy}><Save size={17} />{busy ? 'Saving…' : 'Save branding and profile'}</button>
      </form>

      <form className="surface-section payment-destination-form" onSubmit={addDestination}>
        <div className="section-heading compact"><div><span className="eyebrow">Payments</span><h2>Add payment destination</h2></div><CreditCard size={22} /></div>
        <div className="assurance"><ShieldCheck size={17} /><span>Students see only verified, masked accounts in official payment requests.</span></div>
        <label><span>Destination type</span><FormSelectMenu name="destinationType" ariaLabel="Choose payment destination type" options={[{ value: 'momo', label: 'Mobile Money', description: 'A verified Ghana mobile wallet' }, { value: 'bank', label: 'Bank account', description: 'A verified business bank account' }]} /></label>
        <label><span>Provider</span><input name="provider" list="payment-provider-options" required minLength={2} placeholder="Choose or enter a provider" /><datalist id="payment-provider-options">{paymentProviders.map(provider => <option value={provider} key={provider} />)}</datalist></label>
        <label><span>Account name</span><input name="accountName" required minLength={2} placeholder="Name registered on the account" /></label>
        <label><span>Account number</span><input name="accountNumber" required minLength={5} inputMode="numeric" autoComplete="off" placeholder="Mobile number or bank account number" /></label>
        <label className="check-field"><input name="isDefault" type="checkbox" /><span><strong>Use as default after verification</strong><small>Official payment requests will select this destination first.</small></span></label>
        <button className="primary-button" disabled={busy}><CreditCard size={17} />{busy ? 'Submitting…' : 'Submit for verification'}</button>
      </form>
    </div>

    <div className="agency-settings-grid agency-settings-secondary">
      <section className="surface-section">
        <div className="section-heading compact"><div><span className="eyebrow">Verified destinations</span><h2>Payment accounts</h2></div><ShieldCheck size={22} /></div>
        {destinations.length ? <div className="settings-list">{destinations.map(item => <article key={item.id}><div className="settings-list-icon"><CreditCard /></div><div><strong>{item.provider} · {item.account_name}</strong><p>{item.account_number_masked} · {item.destination_type === 'momo' ? 'Mobile Money' : 'Bank account'}</p></div><span className={`status-pill ${item.verification_status}`}>{item.verification_status}</span></article>)}</div> : <p className="muted">No payment destinations have been added.</p>}
      </section>
      <section className="surface-section">
        <div className="section-heading compact"><div><span className="eyebrow">Access</span><h2>Agency team</h2></div><Users size={22} /></div>
        <div className="settings-list">{members.map(member => <article key={member.user_id}><div className="settings-list-icon"><Users /></div><div><strong>{member.name}</strong><p>{member.email}</p></div><span className="status-pill">{member.role}</span></article>)}</div>
        <form className="inline-invite-form" onSubmit={inviteMember}><label><span>Team member email</span><input name="email" type="email" required placeholder="name@example.com" /></label><label><span>Role</span><FormSelectMenu name="role" ariaLabel="Choose team role" options={[{ value: 'agent', label: 'Agent', description: 'Manage leads and conversations' }, { value: 'manager', label: 'Manager', description: 'Manage operations and invite staff' }]} /></label><button className="secondary-button" disabled={busy}><UserPlus size={17} />Invite member</button></form>
      </section>
    </div>
  </div>;
}
